// src/components/operationCenter/widgets/TweetFeedWidget.tsx
// Widget wrapper for Twitter feed with ticker filter selector

import React, { useState, useCallback, useMemo } from "react";
import type { WidgetProps, TweetFeedConfig } from "@/types/dashboard";
import LatestTweetsCard from "@/components/common/LatestTweetsCard";
import CompactTokenSelector from "./shared/CompactTokenSelector";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";

const TweetFeedWidget: React.FC<WidgetProps<TweetFeedConfig>> = ({ 
  widget, 
  onConfigChange 
}) => {
  const config = widget.config as TweetFeedConfig;
  
  // State - initialized from config
  const [selectedTickers, setSelectedTickers] = useState<string[]>(
    config.tickers ?? []
  );

  // Get available tokens from screener
  const { data: rows } = useTokensScreenerData();
  const availableTokens = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows
      .map(r => String(r["tokenSymbol"] ?? "").toUpperCase())
      .filter(Boolean)
      .slice(0, 100);
  }, [rows]);

  // Handle ticker change
  const handleTickerChange = useCallback((value: string | string[]) => {
    const tickers = Array.isArray(value) ? value : [value];
    setSelectedTickers(tickers);
    onConfigChange?.({ tickers });
  }, [onConfigChange]);
  
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Controls - ticker selector */}
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b border-gray-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
          Tweets
        </span>
        <div className="ml-auto flex items-center gap-2">
          <CompactTokenSelector
            value={selectedTickers}
            onChange={handleTickerChange}
            multiple
            {...(availableTokens.length > 0 ? { tokens: availableTokens } : {})}
            placeholder="Filter by tokens"
          />
          <span className="text-[10px] text-slate-500">
            {selectedTickers.length > 0 
              ? `${selectedTickers.length} token${selectedTickers.length !== 1 ? 's' : ''}` 
              : 'All tweets'}
          </span>
        </div>
      </div>

      {/* Tweet feed */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <LatestTweetsCard 
          fullHeight 
          tickers={selectedTickers} 
          showHeader={false}
          showSentimentFilters={false}
        />
      </div>
    </div>
  );
};

export default TweetFeedWidget;
